import { action, computed, observable } from 'mobx';
import { getReviews } from '../services/get-reviews';
import { InitialStateStore } from './initial-state';
import {
    Tag,
    HotelReviewsReq,
    HotelReview,
    ReviewsAmountByGrade,
    SeasonsType,
    ParamsFilterType,
    MonthIntervalTypes,
    PayloadType,
    HotelReviewSourceType,
    SortByType,
    ReviewOverview,
    ReviewRatingType,
    AdaptiveType,
    RenderedTagType,
    ReviewsSources,
} from '../models/hotel-reviews';
import {
    DEFAULT_SOURCES,
    DEFAULT_OFFSET,
    DEFAULT_REVIEW_AMOUNT_ON_PAGE,
    DEFAULT_MONTH_INTERVALS,
    DEFAULT_SORT_BY,
    INIT_PAYLOAD,
    DEFAULT_REVIEWS_AMOUNT_BY_GRADE,
    DEFAULT_RATING_FILTER,
    DEFAULT_REVIEWS_OVERVIEW,
} from '../consts/reviews';
import { getCheckedMonths, getReviewsAdaptiveType } from '../utils/reviews';

export class HotelReviewsStore {
    reviewAmountOnPage = DEFAULT_REVIEW_AMOUNT_ON_PAGE;

    offset = DEFAULT_OFFSET;

    @observable
    isLoading: boolean = false;

    @observable
    errorReviewsLoading: boolean = false;

    /* Meta data */
    @observable
    totalReviewAmount: number = 0;

    @observable
    reviewsAmountByGrade: ReviewsAmountByGrade =
        DEFAULT_REVIEWS_AMOUNT_BY_GRADE;

    @observable
    foundSources: HotelReviewSourceType[] = [];

    @observable
    availableTags: Array<Tag> = [];

    @observable
    hotelReviews: Array<HotelReview> = [];

    @observable
    reviewsOverview: ReviewOverview = DEFAULT_REVIEWS_OVERVIEW;

    /* Filters & Sort */

    @observable
    filteredReviewAmount: number = 0;

    @observable
    seasonsFilter: Array<SeasonsType> = [];

    @observable
    paramsFilter: Array<ParamsFilterType> = [];

    @observable
    interestsFilter: Array<number> = [];

    @observable
    sourcesFilter: Array<HotelReviewSourceType> = [];

    @observable
    ratingFilter: Array<ReviewRatingType> = [];

    @observable
    sortBy: SortByType = DEFAULT_SORT_BY;

    /* UI */

    @observable
    mobileFiltersOpen = false;

    @observable
    adaptiveView = getReviewsAdaptiveType();

    @observable
    selectedReviewsSources: ReviewsSources = ReviewsSources.Others;

    readonly topHotelsId = '7129c5d0cd69e396b4883cae1226c833';

    constructor(private initialStateStore: InitialStateStore) {
        this.fetchInitReviews();
    }

    @action
    setIsLoading(loading: boolean) {
        this.isLoading = loading;
    }

    @action
    setTotalReviewAmount(count: number) {
        this.totalReviewAmount = count;
    }

    @action
    setFilteredReviewAmount(count: number) {
        this.filteredReviewAmount = count;
    }

    @action
    setReviewsAmountByGrade(reviewsAmountByGrade: ReviewsAmountByGrade) {
        this.reviewsAmountByGrade = reviewsAmountByGrade;
    }

    @action
    setReviewsOverview(reviewsOverview: ReviewOverview) {
        this.reviewsOverview = reviewsOverview;
    }

    @action
    setAvailableTags(availableTags: Array<Tag>) {
        this.availableTags = availableTags;
    }

    @action
    setFoundSources(foundSources: HotelReviewSourceType[]) {
        this.foundSources = foundSources;
    }

    @action
    setErrorReviewsLoading(errorReviewsLoading: boolean) {
        this.errorReviewsLoading = errorReviewsLoading;
    }

    @action
    fetchInitReviews() {
        const hotelId = this.initialStateStore.hotelDetails?.id || null;
        if (hotelId) {
            this.setIsLoading(true);
            getReviews(hotelId, INIT_PAYLOAD)
                .then((hotelReviews: HotelReviewsReq) => {
                    if (hotelReviews.error) {
                        this.setIsLoading(true);
                        this.setErrorReviewsLoading(true);
                    }

                    const reviews = hotelReviews.data?.results;

                    if (reviews) {
                        this.setIsLoading(false);
                        const { data } = hotelReviews;
                        this.setHotelReviews(reviews);

                        this.setTotalReviewAmount(data?.total || 0);
                        this.setFilteredReviewAmount(data?.total || 0);
                        this.setReviewsAmountByGrade(
                            data?.overview?.amountByRatingType ??
                                DEFAULT_REVIEWS_AMOUNT_BY_GRADE
                        );
                        this.setReviewsOverview({
                            ...DEFAULT_REVIEWS_OVERVIEW,
                            ...data?.overview,
                        });
                        this.setAvailableTags(data?.availableTags || []);
                        this.setFoundSources(data?.foundSources || []);
                        this.setErrorReviewsLoading(false);
                    }
                })
                .catch((err: Error) => {
                    console.error(err);
                    this.setIsLoading(false);
                    this.setErrorReviewsLoading(true);
                });
        }
    }

    @action
    getMoreReviews() {
        const hotelId = this.initialStateStore.hotelDetails?.id || null;
        if (hotelId) {
            const offset = this.hotelReviews.length;
            const payload = {
                ...this.getActualRequestParams(),
                offset,
            };
            getReviews(hotelId, payload)
                .then((hotelReviews: HotelReviewsReq) => {
                    const reviews = hotelReviews.data?.results;

                    if (reviews?.length) {
                        const newReviewsList = [
                            ...this.hotelReviews,
                            ...reviews,
                        ];
                        this.setHotelReviews(newReviewsList);
                        this.setErrorReviewsLoading(false);
                    }

                    if (hotelReviews.error) {
                        this.setErrorReviewsLoading(true);
                    }
                })
                .catch(() => this.setErrorReviewsLoading(true));
        }
    }

    @action
    setHotelReviews(hotelReviews: Array<HotelReview>): void {
        this.hotelReviews = hotelReviews;
    }

    @action
    setReviewSortBy(sortType: SortByType): void {
        this.sortBy = sortType;
        this.applySearchFilters();
    }

    @action
    getActualRequestParams(): PayloadType {
        return {
            offset: this.offset * DEFAULT_REVIEW_AMOUNT_ON_PAGE,
            amount: this.reviewAmountOnPage,
            sources: this.sourcesFilter.length
                ? [...this.sourcesFilter]
                : DEFAULT_SOURCES,
            monthIntervals: this.getSeasonsFilter().length
                ? this.getSeasonsFilter()
                : DEFAULT_MONTH_INTERVALS,
            tags: this.interestsFilter,
            ...this.paramsFilters,
            ratingType: this.ratingFilter.length
                ? [...this.ratingFilter]
                : DEFAULT_RATING_FILTER,
            sortBy: this.sortBy,
        };
    }

    @action
    applySearchFilters(): void {
        let hotelId = this.initialStateStore.hotelDetails?.id || null;
        const payload = this.getActualRequestParams();
        if (hotelId) {
            getReviews(hotelId, payload)
                .then((hotelReviews: HotelReviewsReq) => {
                    const reviews = hotelReviews?.data?.results;

                    if (reviews) {
                        this.setHotelReviews(reviews);
                        this.setFilteredReviewAmount(
                            hotelReviews.data?.total || 0
                        );
                        this.setErrorReviewsLoading(false);
                    }

                    if (hotelReviews.error) {
                        this.setErrorReviewsLoading(true);
                    }
                })
                .catch((err: Error) => {
                    console.error(err);
                    this.setErrorReviewsLoading(true);
                });
        }
    }
    // any - т.к это общий метод для изменения checkbox и типы могут быть разные
    @action changeCheckboxFilters(checkedArray: any[], target: any) {
        const isChecked = checkedArray.includes(target);
        return isChecked
            ? checkedArray.filter((el) => el !== target)
            : [...checkedArray, target];
    }

    @action
    setSeasonFilter(season: SeasonsType): void {
        const seasons = this.changeCheckboxFilters(this.seasonsFilter, season);
        this.seasonsFilter = seasons;

        this.applySearchFilters();
    }

  @action
  setRatingFilter(rating: ReviewRatingType): void {
    if (rating === ReviewRatingType.Bad) {
      this.ratingFilter = this.changeCheckboxFilters(this.ratingFilter, ReviewRatingType.Bad);
      this.ratingFilter = this.changeCheckboxFilters(this.ratingFilter, ReviewRatingType.Medium);
      this.applySearchFilters();
      return;
    }
    this.ratingFilter = this.changeCheckboxFilters(this.ratingFilter, rating);
    this.applySearchFilters();
  }

    @action
    setParamsFilter(param: ParamsFilterType): void {
        const params = this.changeCheckboxFilters(this.paramsFilter, param);
        this.paramsFilter = params;

        this.applySearchFilters();
    }

    @action
    setSourcesFilter(source: number): void {
        const sources = this.changeCheckboxFilters(this.sourcesFilter, source);
        this.sourcesFilter = sources;

        this.applySearchFilters();
    }

    @action
    setInterestsFilter(interest: number): void {
        const interests = this.changeCheckboxFilters(
            this.interestsFilter,
            interest
        );
        this.interestsFilter = interests;

        this.applySearchFilters();
    }

    @computed
    get sortedTagsByReviewAmount(): Array<RenderedTagType> {
        const tagsToArray = Object.entries(this.reviewsOverview.tagsCount);
        return tagsToArray.sort((tag1, tag2) => tag2[1] - tag1[1]);
    }

    @action
    resetFilters(): void {
        this.seasonsFilter = [];
        this.ratingFilter = [];
        this.paramsFilter = [];
        this.interestsFilter = [];
        this.sourcesFilter = [];

        this.applySearchFilters();
    }

    @action
    setMobileFiltersOpen(state: boolean): void {
        this.mobileFiltersOpen = state;
    }

    @action
    toggleReviewsSources(value: ReviewsSources): void {
        this.selectedReviewsSources = value;
    }

    @computed
    get renderHotelReviewsAmount(): number {
        return this.hotelReviews.length;
    }

    @action
    getSeasonsFilter(): MonthIntervalTypes[] {
        return getCheckedMonths(this.seasonsFilter);
    }

    @computed
    get reviewsAmountToParamsFilters(): {
        withPhoto: number;
        withDetailedText: number;
        withDetailedRatings: number;
    } {
        return {
            withPhoto: this.reviewsOverview.reviewsWithPhotoAvailable,
            withDetailedText:
                this.reviewsOverview.reviewsWithDetailedTextAvailable,
            withDetailedRatings:
                this.reviewsOverview.reviewsWithDetailedRatingsAvailable,
        };
    }

    @computed
    get paramsFilters(): any {
        if (!this.paramsFilter.length) return;

        return {
            withPhotos: this.paramsFilter.includes(ParamsFilterType.WithPhoto),
            withDetailedText: this.paramsFilter.includes(
                ParamsFilterType.WithDetailedText
            ),
            withDetailedRatings: this.paramsFilter.includes(
                ParamsFilterType.WithDetailedRatings
            ),
        };
    }

    @computed
    get averageReviewsRating(): number {
        return Number(this.reviewsOverview.averageReviewsRating.toFixed(1));
    }

    @computed
    get showReviewsBlock(): boolean {
        return Boolean(
            !this.isLoading &&
                (this.totalReviewAmount || this.errorReviewsLoading)
        );
    }

    @computed
    get hotelWithoutReviews(): boolean {
        return Boolean(
            !this.isLoading &&
                !this.totalReviewAmount &&
                !this.errorReviewsLoading
        );
    }

    @computed
    get isMobile(): boolean {
        return this.adaptiveView === AdaptiveType.IsMobile;
    }

    @computed
    get isTabletL(): boolean {
        return this.adaptiveView === AdaptiveType.IsTabletL;
    }

    @computed
    get isTabletS(): boolean {
        return this.adaptiveView === AdaptiveType.IsTabletS;
    }

    @action
    setSsrInitialStateStore(store: InitialStateStore): void {
        this.initialStateStore = store;

        let hotelId = this.initialStateStore.hotelDetails!.id;
        this.setIsLoading(true);

        getReviews(hotelId, INIT_PAYLOAD).then(
            (hotelReviews: HotelReviewsReq) => {
                const reviews = hotelReviews.data?.results || [];
                this.setHotelReviews(reviews);
                this.setIsLoading(false);
            }
        );
    }

    @computed
    get topHotelsHotelUrl(): string {
        return `https://partner.tophotels.ru/rates/ver7/${
            this.initialStateStore.hotelDetails!.id
        }?uid=${this.topHotelsId}&sec=1`;
    }
}
